// External libraries
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

// Components
import { CustomTooltip } from "./CustomTooltip";
import { Heading, TypographyType, TypographyColor, InfoPanel, Body, BodyColor, BodyType } from "../../ui-elements";
import { useRecoilValue } from "recoil";
import { userCurrencyState } from "../../states";
import { formatLineChartYAxisLabel } from "../../helpers";

interface POILineChartProps {
    data: {
        date: number,
        value: number,
        averageValue: number,
        description: string,
    }[] | any,
    cagrText?: any[]
}

export const POILineChart: React.FC<POILineChartProps> = ({ data, cagrText }) => {
    const selCurrency = useRecoilValue(userCurrencyState);

    // Returns the YAxis dataKey whose last value is larger, so the axis domain isn't clipped.
    const getYAxisKey = (data: any[]) => {
        const { value, averageValue } = data[data.length - 1];
        if (value < averageValue) return 'averageValue';
        return 'value';
    }

    // Returns up to maxTicks evenly spaced tick objects ({ value, label }).
    // Appends "*" to the label for data-incomplete points.
    const getCustomXTicks = (data: any[], maxTicks: number) => {
        if (!data || data.length === 0 || maxTicks < 1) return [];

        const totalPoints = data.length;

        // Appends "*" to the label when the point has incomplete data.
        const buildTick = (item: any) => {
            const numericValue = Number(item.date);
            const label = item.dataIncompleteness ? `${item.date}*` : item.date;
            return { value: numericValue, label };
        };

        if (totalPoints <= maxTicks) {
            return data.map(buildTick);
        }

        // Step ensures index 0 and index totalPoints-1 are always included.
        const step = (totalPoints - 1) / (maxTicks - 1);
        const ticks: any[] = [];

        for (let i = 0; i < maxTicks; i++) {
            ticks.push(buildTick(data[Math.round(i * step)]));
        }

        return ticks;
    };



    return (
        <div className="padding-top-2 padding-bottom-5 padding-left-right-5 w-100">
            <div className="d-flex flex-column justify-content-center align-items-start w-100 h-100 poi-border rounded bg-white">
                <div className="w-100 padding-5 linechart-sm">
                    <div className="d-flex justify-content-start align-items-center padding-bottom-2">
                        <Heading
                            title='Growth of PoI'
                            type={TypographyType.h4}
                            colour={TypographyColor.secondary}
                            classname='text-start p-0'
                        />
                        {/* <AiOutlineInfoCircle className='icon-color-5 margin-left-2 fs-18' /> */}
                        <InfoPanel
                            text='The growth chart is plotted on a subset of PoIs based on the accessibility of the first review date in the meta data of their Google Map marker. Available broadly for a third of the total PoIs in a specific area of interest, it provides a statistically significant and strong signal for growth for any or all categories or sub-categories.\n Since growth rates are based on varying base values, they may look unusually high or low when viewed across longer time periods.'
                            classname='color-black-7 margin-left-2 fs-18'
                        />
                    </div>
                    <ResponsiveContainer width='100%' height="95%" className='position-relative right-6'>
                        {/* key remounts the chart on region change, clearing stale tick DOM elements */}
                        <LineChart
                            key={`${data[0]?.date}-${data[data.length - 1]?.date}-${data.length}`}
                            data={data}
                        >
                            <CartesianGrid horizontal={true} vertical={false} strokeDasharray="3 3" />
                            {/*
                             * interval={0} renders all ticks in the explicit array without Recharts filtering.
                             * tickFormatter maps numeric values back to their display labels (with "*" suffix).
                             */}
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                interval='preserveEnd'
                                className="fs-14 ff-poppins-regular"
                                allowDataOverflow={false}
                                tickCount={4}
                                ticks={getCustomXTicks(data, 4).map(t => t.value)}
                                tickFormatter={(value) => {
                                    const tickObj = getCustomXTicks(data, 4).find(t => t.value === value);
                                    return tickObj?.label ?? value;
                                }}
                            // tick={{ dx: 11 }}

                            />
                            <YAxis
                                dataKey={getYAxisKey(data)}
                                axisLine={false}
                                tickLine={false}
                                className="fs-14 ff-poppins-regular"
                                tickFormatter={(value) => formatLineChartYAxisLabel(value, selCurrency)}
                                allowDataOverflow={false}
                                tickCount={4}
                                tick={{ dy: -2 }}
                                width={50}

                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="rgba(0, 48, 199, 1)"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                            {/* Average value line (dotted) */}
                            {data.some((item: any) => item.averageValue !== null) && (
                                <Line
                                    type="monotone"
                                    dataKey="averageValue"
                                    stroke="rgba(77, 77, 77, 1)"
                                    strokeWidth={2}
                                    strokeDasharray="3 3"
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className="w-100 px-2 py-2 border-top d-flex flex-column justify-content-start align-items-start gap-1">
                    {data.some((item: any) => item.dataIncompleteness) && (
                        <Body
                            color={BodyColor.secondary}
                            type={BodyType.p3}
                            classname=""
                        >
                            {data.find((item: any) => item.dataIncompleteness)?.message}
                        </Body>
                    )}

                    {cagrText?.map((data: any) =>
                    (<div className='d-flex flex-row gap-1'>
                        <span className='col-1 growth-graph-circle bg-black-1 ' style={{ backgroundColor: `${data?.color}`, marginTop: '0.5vh' }}></span>
                        {/* <Heading
                            title={`${data?.label}`}
                            type={TypographyType.h5}
                            colour={TypographyColor.primary}
                        /> */}
                        <Body
                            color={BodyColor.primary}
                            type={BodyType.p3}
                            classname="color-black-16 line-height-16px">
                            {data?.label}
                        </Body>
                    </div>))
                    }
                </div>
            </div>
        </div>
    )
}